import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, Edit, Trash, Plus, Filter, Building, X } from 'lucide-react';
import { inventoryAPI } from '../../services/api';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Alert } from '../common/Alert';

interface InventoryItem {
  id: string;
  panelId: string;
  supplierId: string;
  quantity: number;
  pricePerUnit?: number;
  isActive: boolean;
  panel: {
    id: string;
    SKU: string;
    Description: string;
    Watts_W: number;
    CellType: string;
    ModuleEfficiency_percent: number;
  };
  supplier: {
    id: string;
    companyName: string;
    city: string;
    state: string;
    country: string;
    email: string;
    phoneNumber: string;
  };
}

export const ManageInventory: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBySupplier, setFilterBySupplier] = useState('');
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    panelId: '',
    supplierId: '',
    quantity: '',
    pricePerUnit: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const data = await inventoryAPI.getAllInventory();
      setInventory(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setFormData({
      panelId: item.panelId,
      supplierId: item.supplierId,
      quantity: item.quantity.toString(),
      pricePerUnit: item.pricePerUnit?.toString() || ''
    });
    setShowAddModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await inventoryAPI.updateInventory(editingItem.id, {
          quantity: parseInt(formData.quantity),
          pricePerUnit: formData.pricePerUnit ? parseFloat(formData.pricePerUnit) : undefined
        });
        setSuccess('Inventory updated successfully');
      } else {
        await inventoryAPI.addInventory({
          ...formData,
          quantity: parseInt(formData.quantity),
          pricePerUnit: formData.pricePerUnit ? parseFloat(formData.pricePerUnit) : undefined
        });
        setSuccess('Inventory added successfully');
      }
      setShowAddModal(false);
      setEditingItem(null);
      setFormData({ panelId: '', supplierId: '', quantity: '', pricePerUnit: '' });
      fetchInventory();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save inventory');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        await inventoryAPI.deleteInventory(id);
        setSuccess('Inventory deleted successfully');
        fetchInventory();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete inventory');
      }
    }
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.panel.SKU.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.panel.Description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSupplier = filterBySupplier === '' || item.supplierId === filterBySupplier;
    
    return matchesSearch && matchesSupplier;
  });

  const uniqueSuppliers = Array.from(new Set(inventory.map(item => item.supplier.companyName)))
    .map(name => inventory.find(item => item.supplier.companyName === name)?.supplier)
    .filter(Boolean);

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Inventory Management</h1>
        <p className="text-gray-600">Manage inventory across all suppliers and panels</p>
      </motion.div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by SKU, description, or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="sm:w-64">
            <select
              value={filterBySupplier}
              onChange={(e) => setFilterBySupplier(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F]"
            >
              <option value="">All Suppliers</option>
              {uniqueSuppliers.map(supplier => (
                <option key={supplier!.id} value={supplier!.id}>
                  {supplier!.companyName}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Inventory
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#63B56F] mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading inventory...</p>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No inventory items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Panel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price/Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInventory.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.panel.SKU}</div>
                        <div className="text-sm text-gray-500">{item.panel.Description}</div>
                        <div className="text-xs text-gray-400">
                          {item.panel.Watts_W}W | {item.panel.CellType}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.supplier.companyName}</div>
                        <div className="text-sm text-gray-500">
                          {item.supplier.city}, {item.supplier.state}, {item.supplier.country}
                        </div>
                        <div className="text-xs text-gray-400">{item.supplier.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.quantity}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.pricePerUnit ? `$${Number(item.pricePerUnit).toFixed(2)}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Inventory' : 'Add Inventory'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setFormData({ panelId: '', supplierId: '', quantity: '', pricePerUnit: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {!editingItem && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Panel ID
                    </label>
                    <Input
                      type="text"
                      value={formData.panelId}
                      onChange={(e) => setFormData({ ...formData, panelId: e.target.value })}
                      placeholder="Enter panel ID"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier ID
                    </label>
                    <Input
                      type="text"
                      value={formData.supplierId}
                      onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                      placeholder="Enter supplier ID"
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="Enter quantity"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price per Unit (optional)
                </label>
                <Input
                  type="number"
                  value={formData.pricePerUnit}
                  onChange={(e) => setFormData({ ...formData, pricePerUnit: e.target.value })}
                  placeholder="Enter price per unit"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setFormData({ panelId: '', supplierId: '', quantity: '', pricePerUnit: '' });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingItem ? 'Update' : 'Add'} Inventory
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};